import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Boxes, Loader2, PackagePlus, ScanLine, Search } from "lucide-react";
import ProductPicker from "./ProductPicker";
import DcKitsTab from "./DcKitsTab";
import AccessorySuggestions from "./AccessorySuggestions";

const parseSerials = (t) => [...new Set(t.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean))];

export default function DcAddAsset({ dc, reload }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("serials");
  const [text, setText] = useState("");
  const [picked, setPicked] = useState(null);
  const [qty, setQty] = useState("1");
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const serials = parseSerials(text);
  const reset = () => { setText(""); setPicked(null); setQty("1"); setValue(""); };
  const submit = async () => {
    const declared = value === "" ? null : Number(value);
    if (declared !== null && (Number.isNaN(declared) || declared < 0)) return toast.error("Enter a valid value");
    const body = mode === "serials" ? { serials, declared_inr: declared } : { product_id: picked?.id, qty: Number(qty) || 1, declared_inr: declared };
    setBusy(true);
    try {
      await api.post(`/delivery-challans/${dc.id}/extra-assets`, body);
      toast.success(mode === "serials" ? `${serials.length} device(s) added to the DC` : "Product added to the DC");
      reset(); setOpen(false); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add asset"); }
    finally { setBusy(false); }
  };
  if (!open) {
    return (
      <div className="w-full space-y-2">
        <AccessorySuggestions dc={dc} reload={reload} />
        <Button size="sm" variant="outline" className="h-8 text-[12px]" onClick={() => setOpen(true)} data-testid="dc-add-asset-btn"><PackagePlus className="h-3.5 w-3.5 mr-1" />Add Asset</Button>
      </div>
    );
  }
  const tab = (k, label, Icon) => (
    <button type="button" onClick={() => setMode(k)} data-testid={`dc-add-asset-tab-${k}`}
      className={`inline-flex items-center gap-1 h-7 px-2.5 rounded text-[11px] font-medium transition-colors ${mode === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-white"}`}>
      <Icon className="h-3.5 w-3.5" />{label}
    </button>
  );
  return (
    <div className="w-full min-w-0 rounded-md border border-sky-200 bg-sky-50/40 p-3 space-y-2" data-testid="dc-add-asset-panel">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[10px] font-bold uppercase tracking-wider text-sky-800">Add asset · DC only</span>
        <div className="inline-flex rounded bg-slate-100 p-0.5">{tab("serials", "Scan serials", ScanLine)}{tab("product", "From Product Master", Search)}{tab("kits", "Kits", Boxes)}</div>
        <span className="ml-auto text-[10px] text-slate-500">Not added to the order or invoice</span>
      </div>
      {mode === "kits" ? (
        <>
          <DcKitsTab dc={dc} onApplied={() => { setOpen(false); reload(); }} />
          <div className="flex justify-end"><Button size="sm" variant="ghost" className="h-8 text-[12px]" onClick={() => setOpen(false)} data-testid="dc-add-asset-cancel-kits">Close</Button></div>
        </>
      ) : <>
      {mode === "serials" ? (
        <div className="space-y-1">
          <Textarea autoFocus rows={3} placeholder="Scan or paste stock serials — one per line, or separated by commas / spaces" value={text} onChange={(e) => setText(e.target.value)} className="font-mono text-[12px]" data-testid="dc-add-asset-serials" />
          <div className="text-[11px] text-slate-500" data-testid="dc-add-asset-count">{serials.length} serial(s) · each becomes its own line (qty 1), reserved from stock</div>
        </div>
      ) : (
        <div className="grid gap-2 grid-cols-1 sm:grid-cols-[minmax(0,1fr)_6rem]">
          <ProductPicker picked={picked} setPicked={setPicked} />
          <Input type="number" min="1" max="500" value={qty} onChange={(e) => setQty(e.target.value)} className="h-8 text-[12px]" placeholder="Qty" aria-label="Quantity" data-testid="dc-add-asset-qty" />
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Input type="number" min="0" step="0.01" value={value} onChange={(e) => setValue(e.target.value)} placeholder="Value ₹ per line (optional)" className="h-8 w-56 text-[12px] font-mono" data-testid="dc-add-asset-value" />
        <span className="text-[11px] text-slate-500">Blank = no commercial value</span>
        <div className="ml-auto flex gap-1.5">
          <Button size="sm" variant="ghost" className="h-8 text-[12px]" onClick={() => { reset(); setOpen(false); }} data-testid="dc-add-asset-cancel">Cancel</Button>
          <Button size="sm" className="h-8 text-[12px]" disabled={busy || (mode === "serials" ? !serials.length : !picked)} onClick={submit} data-testid="dc-add-asset-submit">
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <PackagePlus className="h-3.5 w-3.5 mr-1" />}
            {mode === "serials" ? `Add ${serials.length || ""} device${serials.length === 1 ? "" : "s"}` : "Add product"}
          </Button>
        </div>
      </div>
      </>}
    </div>
  );
}
